import { supabase } from '@/integrations/supabase/client';
import { calculateGrossFromNetSync } from '@/utils/fees/calculators/grossAmountCalculator';
import { STRIPE_FIXED_FEE, STRIPE_PERCENT_FEE } from '@/utils/fees/constants';
import { createEmailService } from './emailService';
import { UpgradeEmailData } from '@/types/subscriptionUpgrade';
import { formatAnchorDate } from '@/utils/anchorBilling';

export interface SubscriptionUpgradeData {
  id: string;
  user_id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  current_price_id?: string;
  new_price_id?: string;
  calculated_new_price: number;
  subscription_interval?: string; 
  status: 'pending' | 'email_sent' | 'payment_method_updated' | 'scheduled_for_upgrade' | 'upgraded' | 'failed' | 'cancelled';
  grace_period_end: string;
  email_sent_at?: string;
  payment_method_updated_at?: string;
  upgraded_at?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionUpgradeInput {
  stripe_subscription_id: string;
  stripe_customer_id: string;
  current_price_id?: string;
  current_amount: number; 
  subscription_interval?: string; 
  next_billing_date: string;
  customer_email?: string;
  metadata?: any;
}

export interface BulkUpgradeRequest {
  subscriptions: SubscriptionUpgradeInput[];
}

interface AnchorBillingResult {
  dateLabel: string;
  appliedSubscriptionIds: Set<string>;
}

export interface BulkUpgradePreview {
  subscriptions: Array<{
    stripe_subscription_id: string;
    customer_email: string;
    current_amount: number;
    calculated_new_price: number;
    subscription_interval: string;
    grace_period_end: string;
    processing_fee: number;
    fees_breakdown: {
      grossAmount: number;
      stripeFee: number;
      keepMoreFee: number;
      applicationFee: number;
      currentProcessingFee: number;
    };
  }>;
  total_subscriptions: number;
  total_current_amount: number;
  total_new_amount: number;
  estimated_additional_revenue: number;
  total_processing_fees: number;
  processing_fee_savings: number;
}

class SubscriptionUpgradeService {
  
  /**
   * Preview bulk upgrade to show user what will happen before confirming
   */
  async previewBulkUpgrade(request: BulkUpgradeRequest): Promise<BulkUpgradePreview> {
    try {
      console.log('[SubscriptionUpgradeService] Previewing bulk upgrade for', request.subscriptions.length, 'subscriptions');
      
      const previewPromises = request.subscriptions.map(async (sub) => {
        const feeResult = calculateGrossFromNetSync(sub.current_amount, 'card');
        const stripeAccountId = this.getStripeAccountIdFromMetadata(sub.metadata);
        let subscriptionInterval = sub.subscription_interval;
        
        if (!subscriptionInterval || subscriptionInterval === 'month') {
          try {
            // Only make API call if we have enough time - don't slow down preview too much
            if (request.subscriptions.length <= 10) {
              console.log(`[SubscriptionUpgradeService] Preview: Fetching interval for subscription ${sub.stripe_subscription_id}`);
              
              subscriptionInterval = await this.getSubscriptionIntervalFromStripe(
                sub.stripe_subscription_id,
                stripeAccountId
              );
              
              console.log(`[SubscriptionUpgradeService] Preview: Obtained interval from Stripe API: ${subscriptionInterval}`);
            } else {
              // For large batches, skip API call in preview and use default
              subscriptionInterval = sub.subscription_interval || 'month';
            }
          } catch (intervalError) {
            console.warn(`[SubscriptionUpgradeService] Preview: Error fetching interval from Stripe:`, intervalError);
            // Default to month if API call fails during preview
            subscriptionInterval = sub.subscription_interval || 'month';
          }
        }

        const processingFee = this.calculateCardProcessingFee(sub.current_amount);
        
        return {
          stripe_subscription_id: sub.stripe_subscription_id,
          customer_email: sub.customer_email || this.extractCustomerEmailFromMetadata(sub.metadata),
          current_amount: sub.current_amount,
          calculated_new_price: feeResult.grossAmount,
          subscription_interval: subscriptionInterval,
          grace_period_end: sub.next_billing_date,
          processing_fee: processingFee,
          fees_breakdown: {
            grossAmount: feeResult.grossAmount,
            stripeFee: feeResult.stripeFee,
            keepMoreFee: feeResult.keepMoreFee,
            applicationFee: feeResult.applicationFee,
            currentProcessingFee: processingFee,
          }
        };
      });
      
      // Wait for all promises to resolve
      const previews = await Promise.all(previewPromises);
      
      const totalCurrent = previews.reduce((sum, p) => sum + p.current_amount, 0);
      const totalNew = previews.reduce((sum, p) => sum + p.calculated_new_price, 0);
      const totalProcessingFees = previews.reduce((sum, p) => sum + (p.processing_fee ?? 0), 0);
      const processingFeeSavings = totalProcessingFees;
      
      return {
        subscriptions: previews,
        total_subscriptions: previews.length,
        total_current_amount: totalCurrent,
        total_new_amount: totalNew,
        estimated_additional_revenue: totalNew - totalCurrent,
        total_processing_fees: Number(totalProcessingFees.toFixed(2)),
        processing_fee_savings: Number(processingFeeSavings.toFixed(2)),
      };
    } catch (error) {
      console.error('[SubscriptionUpgradeService] Error previewing bulk upgrade:', error);
      throw error;
    }
  }
  
  /**
   * Execute bulk upgrade - save subscriptions to database
   */
  async executeBulkUpgrade(request: BulkUpgradeRequest): Promise<any[]> {
    try {
      console.log('[SubscriptionUpgradeService] Executing bulk upgrade for', request.subscriptions.length, 'subscriptions');
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      let businessName: string | undefined = undefined;
      let replyTo: string | undefined = undefined;
      let anchorBillingEnabled = false;
      const { data: profile } = await supabase
        .from('profiles')
        .select('business_name, reply_email, anchor_billing_enabled')
        .eq('id', user.id)
        .single();
      if (profile) {
        if (profile.business_name) businessName = profile.business_name;
        replyTo = profile.reply_email || import.meta.env.VITE_FROM_EMAIL;
        anchorBillingEnabled = Boolean(profile.anchor_billing_enabled);
      }

      // When anchor billing is enabled, migrate the selected existing subscriptions
      // to a 1st-of-month billing cycle now (before notifying customers), so the
      // email can communicate the new billing date.
      let anchorBillingResult: AnchorBillingResult | undefined = undefined;
      if (anchorBillingEnabled) {
        anchorBillingResult = await this.applyAnchorBilling(
          request.subscriptions.map(sub => sub.stripe_subscription_id)
        );
      }
      
      // Prepare data for subscription_upgrades table
      const upgradeRecordsPromises = request.subscriptions.map(async (sub) => {
        const feeResult = calculateGrossFromNetSync(sub.current_amount, 'card');
        const stripeAccountId = this.getStripeAccountIdFromMetadata(sub.metadata);
        
        // Ensure we have the subscription interval - use the API method if not provided
        let subscriptionInterval = sub.subscription_interval;
        
        if (!subscriptionInterval || subscriptionInterval === 'month') {
          try {
            // Try to get more accurate interval from Stripe directly
            // Extract Stripe account ID from metadata if available
            console.log(`[SubscriptionUpgradeService] Fetching interval for subscription ${sub.stripe_subscription_id} ${stripeAccountId ? `from account ${stripeAccountId}` : ''}`);
            
            subscriptionInterval = await this.getSubscriptionIntervalFromStripe(
              sub.stripe_subscription_id,
              stripeAccountId
            );
            
            console.log(`[SubscriptionUpgradeService] Obtained interval from Stripe API: ${subscriptionInterval}`);
          } catch (intervalError) {
            console.error(`[SubscriptionUpgradeService] Error fetching interval from Stripe:`, intervalError);
            // Keep the original interval if API call fails
            subscriptionInterval = sub.subscription_interval || 'month';
          }
        }
        
        const processingFee = this.calculateCardProcessingFee(sub.current_amount);
        
        return {
          user_id: user.id,
          stripe_subscription_id: sub.stripe_subscription_id,
          stripe_customer_id: sub.stripe_customer_id,
          current_price_id: sub.current_price_id,
          calculated_new_price: feeResult.grossAmount,
          subscription_interval: subscriptionInterval,
          grace_period_end: sub.next_billing_date,
          status: 'pending' as const,
          metadata: {
            customer_email: sub.customer_email || this.extractCustomerEmailFromMetadata(sub.metadata),
            current_amount: sub.current_amount,
            subscription_interval: subscriptionInterval,
            template: sub.metadata?.template || 'professional',
            fees_breakdown: {
              grossAmount: feeResult.grossAmount,
              stripeFee: feeResult.stripeFee,
              keepMoreFee: feeResult.keepMoreFee,
              applicationFee: feeResult.applicationFee,
              currentProcessingFee: processingFee,
            },
            processing_fee: processingFee,
            original_metadata: sub.metadata
          }
        };
      });
      
      const upgradeRecords = await Promise.all(upgradeRecordsPromises);
      
      const { data, error } = await supabase
        .from('subscription_upgrades')
        .insert(upgradeRecords)
        .select();
      
      if (error) {
        console.error('[SubscriptionUpgradeService] Error saving bulk upgrade:', error);
        throw new Error(`Failed to save subscription upgrades: ${error.message}`);
      }

      // Send email notifications
      const emailService = createEmailService();
      const emailPromises = upgradeRecords.map(async (record, index) => {
        try {
          const emailData: UpgradeEmailData = {
            customerEmail: record.metadata.customer_email,
            customerName: record.metadata.customer_email.split('@')[0] || '',
            subscriptionName: `Subscription ${record.stripe_subscription_id.slice(-6)}`,
            businessName: businessName,
            currentAmount: record.metadata.current_amount,
            newAmount: record.calculated_new_price,
            gracePeriodEnd: record.grace_period_end,
            changePaymentMethodUrl: `${import.meta.env.VITE_APP_URL || 'https://keepmore.co'}/update-payment/${record.stripe_subscription_id}`,
            supportEmail: import.meta.env.VITE_SUPPORT_EMAIL || 'support@keepmore.com',
            template: record.metadata.template || 'professional',
            fromName: businessName,
            replyTo,
            nextBillingDate: anchorBillingResult?.appliedSubscriptionIds.has(record.stripe_subscription_id)
              ? anchorBillingResult.dateLabel
              : undefined
          };

          await emailService.sendUpgradeNotification(emailData);
          
          // Update status to email_sent
          if (data && data[index]) {
            await supabase
              .from('subscription_upgrades')
              .update({ 
                status: 'email_sent',
                email_sent_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              })
              .eq('id', data[index].id);
          }

          console.log(`[SubscriptionUpgradeService] Email sent to ${record.metadata.customer_email}`);
        } catch (emailError) {
          console.error(`[SubscriptionUpgradeService] Failed to send email to ${record.metadata.customer_email}:`, emailError);
          // Don't fail the whole process if email fails
        }
      });

      // Wait for all emails to be processed
      const results = await Promise.allSettled(emailPromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      console.log(`[SubscriptionUpgradeService] Email results: ${successful} sent, ${failed} failed`);
      
      console.log('[SubscriptionUpgradeService] Successfully saved', data.length, 'subscription upgrades to database');
      
      return data;
      
    } catch (error) {
      console.error('[SubscriptionUpgradeService] Error executing bulk upgrade:', error);
      throw error;
    }
  }
  
  /**
   * Get all subscription upgrades for current user
   */
  async getUserSubscriptionUpgrades(status?: string): Promise<any[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      let query = supabase
        .from('subscription_upgrades')
        .select('*')
        .eq('user_id', user.id);
      
      if (status && status !== 'all') {
        query = query.eq('status', status);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error('[SubscriptionUpgradeService] Error getting user subscription upgrades:', error);
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('[SubscriptionUpgradeService] Error getting user subscription upgrades:', error);
      throw error;
    }
  }
  
  /**
   * Update subscription upgrade status
   */
  async updateUpgradeStatus(
    upgradeId: string, 
    status: string,
    additionalFields?: any
  ): Promise<any> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      const updateData: any = {
        status,
        updated_at: new Date().toISOString(),
        ...additionalFields,
      };
      
      // Set timestamp fields based on status
      switch (status) {
        case 'payment_method_updated':
          updateData.payment_method_updated_at = new Date().toISOString();
          break;
        case 'upgraded':
          updateData.upgraded_at = new Date().toISOString();
          break;
      }
      
      const { data, error } = await supabase
        .from('subscription_upgrades')
        .update(updateData)
        .eq('id', upgradeId)
        .eq('user_id', user.id)
        .select()
        .single();
      
      if (error) {
        console.error('[SubscriptionUpgradeService] Error updating upgrade status:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('[SubscriptionUpgradeService] Error updating upgrade status:', error);
      throw error;
    }
  }
  
  /**
   * Cancel subscription upgrade
   */
  async cancelUpgrade(upgradeId: string): Promise<void> {
    try {
      await this.updateUpgradeStatus(upgradeId, 'cancelled');
      console.log('[SubscriptionUpgradeService] Successfully cancelled upgrade:', upgradeId);
    } catch (error) {
      console.error('[SubscriptionUpgradeService] Error cancelling upgrade:', error);
      throw error;
    }
  }
  
  /**
   * Get upgrades that are ready for processing (past grace period)
   */
  async getUpgradesReadyForProcessing(): Promise<any[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('subscription_upgrades')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .lt('grace_period_end', now)
        .order('grace_period_end', { ascending: true });
      
      if (error) {
        console.error('[SubscriptionUpgradeService] Error getting upgrades ready for processing:', error);
        throw error;
      }
      
      return data || [];
      
    } catch (error) {
      console.error('[SubscriptionUpgradeService] Error getting upgrades ready for processing:', error);
      throw error;
    }
  }
  
  /**
   * Migrate existing Stripe subscriptions to a 1st-of-month billing anchor via
   * the apply-anchor-billing edge function. Returns the formatted new billing
   * date label (e.g. "July 1, 2026") for use in the notification email, or
   * undefined if the anchor could not be applied.
   */
  private async applyAnchorBilling(subscriptionIds: string[]): Promise<AnchorBillingResult | undefined> {
    try {
      if (!subscriptionIds || subscriptionIds.length === 0) return undefined;

      const { data, error } = await supabase.functions.invoke('apply-anchor-billing', {
        body: { subscriptionIds }
      });

      if (error) {
        console.error('[SubscriptionUpgradeService] apply-anchor-billing error:', error);
        return undefined;
      }

      if (!data?.success || !data?.anchorDateISO || !Array.isArray(data.results)) {
        console.warn('[SubscriptionUpgradeService] apply-anchor-billing returned no anchor date:', data);
        return undefined;
      }

      const appliedSubscriptionIds = new Set<string>(
        data.results
          .filter((r: { status: string; id: string }) => r.status === 'applied')
          .map((r: { id: string }) => r.id)
      );

      if (appliedSubscriptionIds.size === 0) {
        console.log('[SubscriptionUpgradeService] Anchor billing: all subscriptions skipped or failed');
        return undefined;
      }

      console.log('[SubscriptionUpgradeService] Anchor billing applied:', data.results);
      return {
        dateLabel: formatAnchorDate(new Date(data.anchorDateISO)),
        appliedSubscriptionIds,
      };
    } catch (err) {
      console.error('[SubscriptionUpgradeService] Failed to apply anchor billing:', err);
      return undefined;
    }
  }

  private calculateCardProcessingFee(amount: number): number {
    const safeAmount = typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0;
    const fee = safeAmount * STRIPE_PERCENT_FEE + STRIPE_FIXED_FEE;
    return Number(fee.toFixed(2));
  }

  private getStripeAccountIdFromMetadata(metadata?: any): string | undefined {
    return metadata?.subscription_record?.stripe_account_id ||
      metadata?.stripe_data?.account_id ||
      metadata?.stripe_account_id ||
      undefined;
  }
  
  /**
   * Get subscription interval directly from Stripe API
   * This is a fallback method to ensure we get the correct interval
   */
  async getSubscriptionIntervalFromStripe(
    subscriptionId: string, 
    stripeAccountId?: string
  ): Promise<string> {
    try {
      console.log(`[SubscriptionUpgradeService] Fetching subscription interval directly from Stripe for: ${subscriptionId}`);
      
      // Use Edge Function to call Stripe API (avoids exposing Stripe keys in client)
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-subscription-interval`;
      
      // Get the current session for authorization
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription_id: subscriptionId,
          stripe_account_id: stripeAccountId
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch subscription interval: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!data || !data.interval) {
        throw new Error('Invalid response from subscription interval service');
      }
      
      console.log(`[SubscriptionUpgradeService] Successfully retrieved interval: ${data.interval}`);
      return data.interval;
      
    } catch (error) {
      console.error('[SubscriptionUpgradeService] Error fetching subscription interval:', error);
      // Default to month if we can't fetch from API
      return 'month';
    }
  }
  
  /**
   * Helper to extract customer email from metadata
   */
  private extractCustomerEmailFromMetadata(metadata?: any): string {
    if (!metadata) return 'N/A';
    
    return metadata.customer_email || 
           metadata.customerEmail || 
           metadata.customer?.email || 
           'N/A';
  }
}

export const subscriptionUpgradeService = new SubscriptionUpgradeService();